<script>
  import { onMount } from 'svelte';
  import { router } from '@inertiajs/svelte';
  import ApplicationLayout from '@/components/ApplicationLayout/index.svelte';
  import Button from '@/components/Button.svelte';
  
  let { property, pending_images = [], user = {} } = $props();
  
  let currentImageIndex = $state(0);
  let showCamera = $state(false);
  let videoStream = null;
  let videoElement = $state(null);
  
  async function assignToItem() {
    const image = pending_images[currentImageIndex];
    try {
      const response = await fetch(`/properties/${property.id}/pending_images/${image.id}/assign_to_item`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').content
        }
      });
      
      if (response.ok) {
        moveToNextImage();
      } else {
        const data = await response.json();
        alert(data.errors || 'Failed to assign image to item');
      }
    } catch (error) {
      console.error('Error:', error);
      alert('Failed to assign image to item');
    }
  }
  
  async function assignToContainer() {
    const image = pending_images[currentImageIndex];
    try {
      const response = await fetch(`/properties/${property.id}/pending_images/${image.id}/assign_to_container`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').content
        }
      });
      
      if (response.ok) {
        moveToNextImage();
      } else {
        const data = await response.json();
        alert(data.errors || 'Failed to assign image to container');
      }
    } catch (error) {
      console.error('Error:', error);
      alert('Failed to assign image to container');
    }
  }
  
  async function deleteImage() {
    const image = pending_images[currentImageIndex];
    try {
      const response = await fetch(`/properties/${property.id}/pending_images/${image.id}`, {
        method: 'DELETE',
        headers: {
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').content
        }
      });
      
      if (response.ok) {
        moveToNextImage();
      } else {
        alert('Failed to delete image');
      }
    } catch (error) {
      console.error('Error:', error);
      alert('Failed to delete image');
    }
  }
  
  function moveToNextImage() {
    if (currentImageIndex < pending_images.length - 1) {
      currentImageIndex++;
    }
  }
  
  async function startCamera() {
    try {
      videoStream = await navigator.mediaDevices.getUserMedia({ video: true });
      videoElement.srcObject = videoStream;
      showCamera = true;
    } catch (error) {
      console.error('Error accessing camera:', error);
      alert('Failed to access camera');
    }
  }
  
  function stopCamera() {
    if (videoStream) {
      videoStream.getTracks().forEach(track => track.stop());
      videoStream = null;
    }
    showCamera = false;
  }
  
  async function capturePhoto() {
    const canvas = document.createElement('canvas');
    canvas.width = videoElement.videoWidth;
    canvas.height = videoElement.videoHeight;
    canvas.getContext('2d').drawImage(videoElement, 0, 0);
    
    canvas.toBlob(async (blob) => {
      const formData = new FormData();
      formData.append('image', blob, 'photo.jpg');
      
      try {
        const response = await fetch(`/properties/${property.id}/pending_images`, {
          method: 'POST',
          headers: {
            'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').content
          },
          body: formData
        });
        
        if (response.ok) {
          stopCamera();
          router.reload();
        } else {
          const data = await response.json();
          alert(data.errors || 'Failed to upload photo');
        }
      } catch (error) {
        console.error('Error:', error);
        alert('Failed to upload photo');
      }
    }, 'image/jpeg');
  }
  
  function handleFileUpload(event) {
    const files = event.target.files;
    if (!files.length) return;
    
    const formData = new FormData();
    Array.from(files).forEach(file => {
      formData.append('image', file);
    });
    
    fetch(`/properties/${property.id}/pending_images`, {
      method: 'POST',
      headers: {
        'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').content
      },
      body: formData
    })
    .then(response => response.json())
    .then(data => {
      if (data.errors) {
        alert(data.errors);
      } else {
        router.reload();
      }
    })
    .catch(error => {
      console.error('Error:', error);
      alert('Failed to upload images');
    });
  }
</script>

<svelte:head>
  <title>Quick Capture - {property.name} | Maset</title>
</svelte:head>

<ApplicationLayout {user}>
  <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
    <!-- Property Header -->
    <div class="relative rounded-2xl overflow-hidden mb-8">
      <div class="absolute inset-0 bg-gradient-to-br from-brand-900 via-brand-700 to-teal-600"></div>
      <div class="relative px-6 py-8 sm:px-8 sm:py-12">
        <div class="flex justify-between items-start">
          <div>
            <h1 class="text-3xl font-bold text-white mb-2">Quick Capture</h1>
            <p class="text-brand-100 text-lg">
              Capture and organize items for {property.name}
            </p>
            <p class="text-brand-50 text-sm mt-1">
              Take multiple photos and quickly sort them into items or containers
            </p>
          </div>
          <div class="flex items-center space-x-4">
            <a href={`/properties/${property.id}`} class="text-white hover:text-white/80 font-medium inline-flex items-center">
              <svg class="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 19l-7-7m0 0l7-7m-7 7h18" />
              </svg>
              Back to Property
            </a>
          </div>
        </div>
      </div>
    </div>

    <!-- Main Content -->
    <div class="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
      <div class="p-6">
        <div class="upload-section mb-8">
          <div class="flex space-x-4 justify-center">
            <Button variant="outline" onclick={startCamera}>
              <svg class="h-5 w-5 mr-2" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
              Take Photo
            </Button>
            
            <div class="relative">
              <input
                type="file"
                accept="image/*"
                multiple
                onchange={handleFileUpload}
                class="hidden"
                id="file-upload"
              />
              <Button variant="outline" onclick={() => document.getElementById('file-upload').click()}>
                <svg class="h-5 w-5 mr-2" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
                </svg>
                Upload Images (max 15)
              </Button>
            </div>
          </div>
        </div>
        
        {#if showCamera}
          <div class="camera-container mb-8">
            <!-- Live camera preview: muted, no audio track to caption -->
            <video
              bind:this={videoElement}
              autoplay
              playsinline
              muted
              class="w-full max-w-lg mx-auto rounded-lg shadow-lg"
            ></video>
            <div class="flex justify-center space-x-4 mt-4">
              <Button variant="outline" onclick={capturePhoto}>
                Capture
              </Button>
              <Button variant="outline" onclick={stopCamera}>
                Cancel
              </Button>
            </div>
          </div>
        {/if}
        
        <div class="image-stack">
          {#if pending_images.length > 0}
            <div class="image-card">
              <img src={pending_images[currentImageIndex].image_url} alt="Pending upload awaiting review" />
              <div class="progress">
                {currentImageIndex + 1} / {pending_images.length}
              </div>
            </div>
          {:else}
            <div class="no-images">
              No pending images. Take a photo or upload images to get started.
            </div>
          {/if}
        </div>

        {#if pending_images.length > 0}
          <div class="action-buttons">
            <Button variant="outline" onclick={assignToItem}>
              <svg class="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
              </svg>
              Item
            </Button>
            <Button variant="outline" onclick={assignToContainer}>
              <svg class="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
              </svg>
              Container
            </Button>
            <Button variant="outline" onclick={deleteImage}>
              <svg class="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
              </svg>
              Delete
            </Button>
          </div>
        {/if}
      </div>
    </div>
  </div>
</ApplicationLayout>

<style>
  .image-stack {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 12px;
    margin-bottom: 32px;
  }
  
  .image-card {
    width: 95%;
    max-width: 500px;
    height: 450px;
    transition: transform 0.1s ease-out;
    transform-style: preserve-3d;
    transform: translateX(var(--current-x, 0px)) rotateY(calc(var(--current-x, 0) * 0.05deg));
    background: white;
    border-radius: 12px;
    box-shadow: 0 4px 6px rgba(0,0,0,0.1), 0 1px 3px rgba(0,0,0,0.08);
    padding: 4px;
    display: flex;
    align-items: center;
    justify-content: center;
  }
  
  @media (min-width: 640px) {
    .image-card {
      width: 80%;
    }
  }
  
  .image-card img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    border-radius: 8px;
  }
  
  .action-buttons {
    display: flex;
    justify-content: center;
    gap: 0.75rem;
    padding: 0 20px;
  }
  
  .progress {
    position: absolute;
    top: 10px;
    right: 10px;
    background-color: rgba(0,0,0,0.5);
    color: white;
    padding: 5px 10px;
    border-radius: 4px;
    z-index: 10;
  }
  
  .no-images {
    text-align: center;
    color: #666;
    font-size: 1.2em;
  }
</style> 